import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2, Copy, Mail, CheckCircle2, Link as LinkIcon } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL;

function headers() {
  var t = localStorage.getItem('admin_token');
  return t ? { Authorization: 'Bearer ' + t } : {};
}

function jsonHeaders() {
  return Object.assign({ 'Content-Type': 'application/json' }, headers());
}

function money(n) {
  return '$' + Number(n || 0).toFixed(2);
}

var EMPTY_ADDRESS = {
  first_name: '', last_name: '', phone: '', street_address: '', extended_address: '',
  locality: '', region: '', postal_code: '', country_code_alpha2: 'US',
};

function normalizeSearch(text) {
  return (text || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

var FIELD = 'w-full bg-black border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#d4af37]';
var LABEL = 'block text-neutral-400 text-xs font-semibold uppercase tracking-wide mb-1.5';

// Products like the NoteTurner carry option groups (INSTRUMENT, BATTERY) and
// some options add a surcharge, written in the label as "(+$9.99)".
function variantGroups(product) {
  var groups = ((product || {}).variants || {}).groups;
  return Array.isArray(groups) ? groups : [];
}

function surcharge(option) {
  var m = /\(\+\s*\$?([\d.]+)\)/.exec(option || '');
  return m ? Number(m[1]) : 0;
}

function variantLabel(picks, groups) {
  return groups.map(function(g) { return picks[g.name]; })
    .filter(Boolean).join(' · ');
}

function priceWithVariants(line) {
  var base = Number(line.base_price != null ? line.base_price : line.unit_price) || 0;
  var extra = (line.groups || []).reduce(function(s, g) {
    return s + surcharge((line.picks || {})[g.name]);
  }, 0);
  return Math.round((base + extra) * 100) / 100;
}

// Take an order over the phone or at a gig: pick items, set shipping/tax/discount,
// then either send a PayPal link or record cash / bank transfer.
export default function ManualOrderPanel() {
  var [email, setEmail] = useState('');
  var [name, setName] = useState('');
  var [phone, setPhone] = useState('');
  var [language, setLanguage] = useState('en');
  var [query, setQuery] = useState('');
  var [catalog, setCatalog] = useState([]);
  var [loadingCatalog, setLoadingCatalog] = useState(false);
  var [checked, setChecked] = useState([]);
  var [onlyDigital, setOnlyDigital] = useState('all');
  var [lines, setLines] = useState([]);
  var [address, setAddress] = useState(EMPTY_ADDRESS);
  var [shippingOptions, setShippingOptions] = useState([]);
  var [shippingId, setShippingId] = useState('');
  var [shippingName, setShippingName] = useState('');
  var [shippingCost, setShippingCost] = useState('0');
  var [taxMode, setTaxMode] = useState('amount');  // 'amount' | 'rate'
  var [taxValue, setTaxValue] = useState('0');
  var [suggestedRate, setSuggestedRate] = useState(0);
  var [discount, setDiscount] = useState('0');
  var [discountMode, setDiscountMode] = useState('amount');
  var [pending, setPending] = useState([]);
  var [editing, setEditing] = useState('');
  var [method, setMethod] = useState('paypal');
  var [emailLink, setEmailLink] = useState(true);
  var [markPaid, setMarkPaid] = useState(false);
  var [notes, setNotes] = useState('');
  var [busy, setBusy] = useState('');
  var [msg, setMsg] = useState(null);
  var [created, setCreated] = useState(null);

  var hasPhysical = lines.some(function(l) { return !l.is_digital; });
  var subtotal = lines.reduce(function(s, l) { return s + Number(l.unit_price) * Number(l.quantity); }, 0);
  var physicalSubtotal = lines.reduce(function(s, l) {
    return l.is_digital ? s : s + Number(l.unit_price) * Number(l.quantity);
  }, 0);
  var shipNum = Number(shippingCost) || 0;
  var discountNum = discountMode === 'percent'
    ? Math.round(subtotal * (Number(discount) || 0)) / 100
    : (Number(discount) || 0);
  var taxNum = taxMode === 'amount'
    ? (Number(taxValue) || 0)
    : Math.round((physicalSubtotal + shipNum) * (Number(taxValue) || 0) / 100 * 100) / 100;
  var total = Math.max(0, subtotal + shipNum + taxNum - discountNum);

  // Match on words, ignoring punctuation: "real book vol 4" has to find
  // "Real Book Vol. 4 in C" and the slug real-book-vol-4-c-mobilesheets.
  var terms = normalizeSearch(query).split(' ').filter(Boolean);
  var filtered = catalog.filter(function(p) {
    if (onlyDigital === 'digital' && !p.isDigital) return false;
    if (onlyDigital === 'physical' && p.isDigital) return false;
    if (!terms.length) return true;
    var hay = ' ' + normalizeSearch((p.name || '') + ' ' + (p.slug || '')) + ' ';
    for (var i = 0; i < terms.length; i++) {
      var term = terms[i];
      // Short tokens like "c", "bb" or "eb" are keys — match them as whole
      // words so "vol 4 c" doesn't also pull in "bass clef".
      var hit = term.length <= 2 ? hay.indexOf(' ' + term + ' ') !== -1 : hay.indexOf(term) !== -1;
      if (!hit) return false;
    }
    return true;
  });

  async function loadCatalog() {
    setLoadingCatalog(true);
    try {
      var r = await fetch(API + '/api/admin/catalog', { headers: headers() });
      var d = await r.json().catch(function() { return {}; });
      setCatalog(d.products || []);
    } finally {
      setLoadingCatalog(false);
    }
  }

  useEffect(function() { loadCatalog(); loadPending(); }, []);

  async function loadPending() {
    var r = await fetch(API + '/api/manual-orders/pending', { headers: headers() });
    var d = await r.json().catch(function() { return {}; });
    setPending(d.orders || []);
  }

  async function openOrder(orderNumber) {
    if (!orderNumber) { reset(); return; }
    setBusy('load'); setMsg(null);
    try {
      var r = await fetch(API + '/api/manual-orders/' + orderNumber + '/edit', { headers: headers() });
      var o = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(o.detail || 'Could not open that order');
      setEditing(orderNumber);
      setCreated(null);
      setEmail(o.customer_email || '');
      setName(o.customer_name || '');
      setPhone(o.customer_phone || '');
      setLanguage(o.language || 'en');
      setLines((o.items || []).map(function(i) {
        var p = catalog.find(function(c) { return c.slug === i.slug; }) || {};
        var groups = variantGroups(p);
        var picks = {};
        var saved = (i.variant || '').split('·').map(function(s) { return s.trim(); });
        groups.forEach(function(g) {
          picks[g.name] = (g.options || []).find(function(opt) { return saved.indexOf(opt) !== -1; })
            || (g.options || [])[0] || '';
        });
        return {
          slug: i.slug, name: i.name, quantity: i.quantity,
          unit_price: i.unit_price, base_price: Number(p.price) || i.unit_price,
          is_digital: !!i.is_digital, presale: !!i.presale,
          groups: groups, picks: picks,
          variant: groups.length ? variantLabel(picks, groups) : (i.variant || ''),
        };
      }));
      setAddress(Object.assign({}, EMPTY_ADDRESS, o.shipping_address || {}));
      setShippingId(o.shipping_method_id || '');
      setShippingName(o.shipping_method_name || '');
      setShippingCost(String(o.shipping_cost || 0));
      setTaxMode('amount');
      setTaxValue(String(o.tax_amount || 0));
      if (o.discount_percent) {
        setDiscountMode('percent');
        setDiscount(String(Math.round(o.discount_percent * 10000) / 100));
      } else {
        setDiscountMode('amount');
        setDiscount(String(o.discount_amount || 0));
      }
      setMethod(o.payment_method_type || 'paypal');
      setNotes(o.notes || '');
      setMsg({ type: 'success', text: 'Editing ' + orderNumber + ' — change anything and save' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  async function saveEdits(resendLink) {
    setBusy('save'); setMsg(null);
    var body = {
      customer_email: email.trim(),
      customer_name: name.trim() || null,
      customer_phone: phone.trim() || null,
      language: language,
      items: itemsPayload(),
      shipping_address: hasPhysical ? address : null,
      shipping_method_id: shippingId || null,
      shipping_method_name: shippingName || null,
      shipping_cost: shipNum,
      payment_method: method,
      notes: notes.trim() || null,
      resend_pay_link: !!resendLink,
    };
    if (discountMode === 'percent') {
      body.discount_percent = (Number(discount) || 0) / 100;
      body.discount_amount = 0;
    } else {
      body.discount_amount = Number(discount) || 0;
      body.discount_percent = 0;
    }
    if (taxMode === 'amount') { body.tax_amount = Number(taxValue) || 0; }
    else { body.tax_rate = (Number(taxValue) || 0) / 100; }
    try {
      var r = await fetch(API + '/api/manual-orders/' + editing, {
        method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify(body),
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not save the changes');
      await loadPending();
      setMsg({
        type: 'success',
        text: editing + ' updated — new total ' + money(d.total) +
          (resendLink ? (d.pay_link_emailed ? ' · pay link re-sent' : ' · pay link email FAILED') : ''),
      });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  function toggleCheck(slug) {
    setChecked(function(prev) {
      return prev.indexOf(slug) === -1 ? prev.concat([slug]) : prev.filter(function(s) { return s !== slug; });
    });
  }

  function addChecked() {
    var bySlug = {};
    catalog.forEach(function(p) { bySlug[p.slug] = p; });
    setLines(function(prev) {
      var have = prev.map(function(l) { return l.slug; });
      var additions = checked.filter(function(s) { return have.indexOf(s) === -1; }).map(function(s) {
        var p = bySlug[s] || {};
        var groups = variantGroups(p);
        var picks = {};
        groups.forEach(function(g) { picks[g.name] = (g.options || [])[0] || ''; });
        return {
          slug: s, name: p.name || s, quantity: 1,
          unit_price: Number(p.price) || 0, base_price: Number(p.price) || 0,
          is_digital: !!p.isDigital, groups: groups, picks: picks,
          variant: groups.length ? variantLabel(picks, groups) : '',
        };
      });
      // A variant surcharge like "(+$9.99)" is applied straight away.
      additions.forEach(function(l) { l.unit_price = priceWithVariants(l); });
      return prev.concat(additions);
    });
    setChecked([]);
    setQuery('');
  }

  function pickVariant(idx, groupName, option) {
    setLines(function(prev) {
      return prev.map(function(l, i) {
        if (i !== idx) return l;
        var picks = Object.assign({}, l.picks, { [groupName]: option });
        var next = Object.assign({}, l, { picks: picks, variant: variantLabel(picks, l.groups) });
        next.unit_price = priceWithVariants(next);
        return next;
      });
    });
  }

  function patchLine(idx, field, value) {
    setLines(function(prev) {
      return prev.map(function(l, i) { return i === idx ? Object.assign({}, l, { [field]: value }) : l; });
    });
  }

  function removeLine(idx) {
    setLines(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
  }

  function itemsPayload() {
    return lines.map(function(l) {
      return {
        slug: l.slug,
        quantity: Number(l.quantity) || 1,
        unit_price: Number(l.unit_price),
        variant: (l.variant || '').trim() || null,
      };
    });
  }

  async function getShippingQuote() {
    setBusy('quote'); setMsg(null);
    try {
      var r = await fetch(API + '/api/manual-orders/quote', {
        method: 'POST', headers: jsonHeaders(),
        body: JSON.stringify({ items: itemsPayload(), shipping_address: address }),
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not get rates');
      setShippingOptions(d.shipping_options || []);
      setSuggestedRate(d.suggested_tax_rate || 0);
      if ((d.shipping_options || []).length) {
        setShippingId(d.shipping_options[0].id);
        setShippingName(d.shipping_options[0].name);
        setShippingCost(String(d.shipping_options[0].rate_usd));
      }
      if (d.suggested_tax_rate) {
        setTaxMode('rate');
        setTaxValue(String(Math.round(d.suggested_tax_rate * 10000) / 100));
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  function pickShipping(id) {
    var opt = shippingOptions.find(function(o) { return o.id === id; });
    setShippingId(id);
    if (opt) { setShippingName(opt.name); setShippingCost(String(opt.rate_usd)); }
  }

  async function createOrder() {
    if (!email.trim()) { setMsg({ type: 'error', text: 'Customer email is required' }); return; }
    if (!lines.length) { setMsg({ type: 'error', text: 'Add at least one item' }); return; }
    setBusy('create'); setMsg(null);
    var body = {
      customer_email: email.trim(),
      customer_name: name.trim() || null,
      customer_phone: phone.trim() || null,
      language: language,
      items: itemsPayload(),
      shipping_address: hasPhysical ? address : null,
      shipping_method_id: shippingId || null,
      shipping_method_name: shippingName || null,
      shipping_cost: shipNum,
      discount_amount: discountMode === 'amount' ? (Number(discount) || 0) : 0,
      discount_percent: discountMode === 'percent' ? (Number(discount) || 0) / 100 : null,
      payment_method: method,
      notes: notes.trim() || null,
      mark_paid: markPaid,
      email_pay_link: emailLink && !markPaid,
    };
    if (taxMode === 'amount') {
      body.tax_amount = Number(taxValue) || 0;
    } else {
      body.tax_rate = (Number(taxValue) || 0) / 100;
    }
    try {
      var r = await fetch(API + '/api/manual-orders', {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body),
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not create the order');
      setCreated(d);
      loadPending();
      setMsg({
        type: 'success',
        text: d.status === 'pending_payment'
          ? 'Order ' + d.order_number + ' created — ' + money(d.total) + ' awaiting payment'
          : 'Order ' + d.order_number + ' is paid' + (d.email_sent ? ' and the receipt was emailed' : ''),
      });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  async function markCreatedPaid() {
    setBusy('paid'); setMsg(null);
    try {
      var r = await fetch(API + '/api/manual-orders/' + created.order_number + '/mark-paid', {
        method: 'POST', headers: jsonHeaders(),
        body: JSON.stringify({ method: method, send_email: true }),
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not confirm payment');
      setCreated(Object.assign({}, created, { status: d.status, download_links: d.download_links }));
      setMsg({ type: 'success', text: 'Payment confirmed — receipt and download links sent' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  async function emailTheLink() {
    setBusy('link');
    try {
      var r = await fetch(API + '/api/manual-orders/' + created.order_number + '/send-pay-link', {
        method: 'POST', headers: jsonHeaders(),
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not send the link');
      setMsg({ type: 'success', text: 'Pay link emailed to ' + email });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  function reset() {
    setCreated(null); setEditing(''); setLines([]); setEmail(''); setName(''); setPhone('');
    setShippingOptions([]); setShippingId(''); setShippingName(''); setShippingCost('0');
    setTaxValue('0'); setDiscount('0'); setDiscountMode('amount'); setNotes('');
    setMsg(null); setMarkPaid(false); setChecked([]); setAddress(EMPTY_ADDRESS);
  }

  return React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-5', 'data-testid': 'manual-order-panel' },
    React.createElement('h2', { className: 'text-white text-lg font-semibold mb-1' }, 'Take an order'),
    React.createElement('p', { className: 'text-neutral-500 text-sm mb-4' }, 'Build an order for a customer, then send a PayPal link or record cash / bank transfer. Paid orders deliver download links automatically.'),

    React.createElement('div', { className: 'flex flex-wrap items-center gap-2 mb-5 bg-black border border-white/10 rounded-xl px-3 py-2.5' },
      React.createElement('span', { className: 'text-neutral-400 text-xs' }, 'Unpaid orders'),
      React.createElement('select', {
        value: editing, onChange: function(e) { openOrder(e.target.value); },
        'data-testid': 'mo-open-order',
        className: 'flex-1 min-w-[180px] bg-neutral-950 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white'
      },
        React.createElement('option', { value: '' }, '— start a new order —'),
        pending.map(function(o) {
          return React.createElement('option', { key: o.order_number, value: o.order_number },
            o.order_number + ' · ' + (o.customer_email || '') + ' · ' + money(o.total));
        })
      ),
      editing ? React.createElement('span', { className: 'text-[#d4af37] text-xs font-semibold', 'data-testid': 'mo-editing-badge' }, 'editing ' + editing) : null
    ),

    // ---- customer ----
    React.createElement('div', { className: 'grid sm:grid-cols-2 gap-3 mb-5' },
      React.createElement('div', null,
        React.createElement('label', { className: LABEL }, 'Customer email *'),
        React.createElement('input', { value: email, onChange: function(e) { setEmail(e.target.value); }, type: 'email', placeholder: 'name@email.com', className: FIELD, 'data-testid': 'mo-email' })
      ),
      React.createElement('div', null,
        React.createElement('label', { className: LABEL }, 'Customer name'),
        React.createElement('input', { value: name, onChange: function(e) { setName(e.target.value); }, className: FIELD, 'data-testid': 'mo-name' })
      ),
      React.createElement('div', null,
        React.createElement('label', { className: LABEL }, 'Phone'),
        React.createElement('input', { value: phone, onChange: function(e) { setPhone(e.target.value); }, className: FIELD, 'data-testid': 'mo-phone' })
      ),
      React.createElement('div', null,
        React.createElement('label', { className: LABEL }, 'Email language'),
        React.createElement('select', { value: language, onChange: function(e) { setLanguage(e.target.value); }, className: FIELD, 'data-testid': 'mo-language' },
          ['en', 'es', 'fr', 'de', 'it', 'ja', 'uk'].map(function(l) {
            return React.createElement('option', { key: l, value: l }, l.toUpperCase());
          })
        )
      )
    ),

    // ---- items (bulk select) ----
    React.createElement('label', { className: LABEL }, 'Items — tick as many as you need, then add them all at once'),
    React.createElement('div', { className: 'flex flex-wrap gap-2 mb-2' },
      React.createElement('input', {
        value: query,
        onChange: function(e) { setQuery(e.target.value); },
        placeholder: 'Filter the catalog (name or slug)…',
        className: FIELD + ' flex-1 min-w-[200px]', 'data-testid': 'mo-item-search'
      }),
      React.createElement('select', {
        value: onlyDigital, onChange: function(e) { setOnlyDigital(e.target.value); },
        'data-testid': 'mo-item-kind',
        className: 'bg-black border border-white/15 rounded-lg px-3 py-2 text-sm text-white'
      },
        React.createElement('option', { value: 'all' }, 'All products'),
        React.createElement('option', { value: 'digital' }, 'Digital only'),
        React.createElement('option', { value: 'physical' }, 'Physical only')
      )
    ),
    React.createElement('div', { className: 'flex flex-wrap items-center gap-2 mb-2' },
      React.createElement('button', {
        type: 'button', onClick: function() { setChecked(filtered.map(function(p) { return p.slug; })); },
        'data-testid': 'mo-select-all',
        className: 'text-xs text-neutral-300 border border-white/15 hover:border-white/40 rounded-full px-3 py-1.5'
      }, 'Select all ' + filtered.length + ' shown'),
      React.createElement('button', {
        type: 'button', onClick: function() { setChecked([]); },
        'data-testid': 'mo-select-none',
        className: 'text-xs text-neutral-400 border border-white/15 hover:border-white/40 rounded-full px-3 py-1.5'
      }, 'Clear'),
      React.createElement('button', {
        type: 'button', onClick: addChecked, disabled: !checked.length,
        'data-testid': 'mo-add-selected',
        className: 'ml-auto flex items-center gap-1.5 bg-[#d4af37] text-black text-xs font-semibold rounded-full px-4 py-2 hover:bg-white disabled:opacity-40'
      }, React.createElement(Plus, { className: 'w-3.5 h-3.5' }),
        checked.length ? 'Add ' + checked.length + ' selected' : 'Nothing ticked')
    ),
    React.createElement('div', {
      className: 'bg-black border border-white/10 rounded-lg mb-4 max-h-72 overflow-y-auto divide-y divide-white/5',
      'data-testid': 'mo-catalog-list'
    },
      loadingCatalog ? React.createElement('p', { className: 'px-3 py-6 text-center text-neutral-500 text-sm' },
        React.createElement(Loader2, { className: 'w-4 h-4 animate-spin inline mr-2' }), 'Loading the catalog…'
      ) : filtered.length ? filtered.map(function(p) {
        var on = checked.indexOf(p.slug) !== -1;
        var inOrder = lines.some(function(l) { return l.slug === p.slug; });
        return React.createElement('label', {
          key: p.slug,
          'data-testid': 'mo-catalog-row',
          className: 'flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5 ' + (on ? 'bg-[#d4af37]/10' : '')
        },
          React.createElement('input', {
            type: 'checkbox', checked: on, disabled: inOrder,
            onChange: function() { toggleCheck(p.slug); },
            'data-testid': 'mo-catalog-check',
            className: 'w-4 h-4 accent-[#d4af37] shrink-0'
          }),
          React.createElement('span', { className: 'flex-1 min-w-0 flex items-center gap-2' },
            React.createElement('span', { className: 'text-sm text-neutral-200 truncate' }, p.name),
            p.hidden ? React.createElement('span', { className: 'text-amber-400/90 text-xs shrink-0' },
              (p.isDigital && !p.dropbox_path) ? 'pre-sale' : 'unlisted') : null,
            React.createElement('span', { className: 'hidden sm:inline text-neutral-600 text-xs shrink-0' }, p.isDigital ? 'digital' : 'physical'),
            inOrder ? React.createElement('span', { className: 'text-emerald-400 text-xs shrink-0' }, 'in order') : null
          ),
          React.createElement('span', { className: 'text-neutral-400 text-sm shrink-0' }, money(p.price))
        );
      }) : React.createElement('p', { className: 'px-3 py-6 text-center text-neutral-600 text-sm' }, 'Nothing matches that filter')
    ),

    lines.length ? React.createElement('div', { className: 'mb-5' },
      React.createElement('div', { className: 'flex items-center justify-between mb-1.5' },
        React.createElement('span', { className: 'text-neutral-400 text-xs' , 'data-testid': 'mo-line-count' },
          lines.length + ' item' + (lines.length === 1 ? '' : 's') + ' in this order'),
        React.createElement('button', {
          type: 'button', onClick: function() { setLines([]); }, 'data-testid': 'mo-clear-lines',
          className: 'text-neutral-500 hover:text-red-300 text-xs underline'
        }, 'Remove all')
      ),
      React.createElement('div', { className: 'space-y-2 max-h-80 overflow-y-auto pr-1', 'data-testid': 'mo-lines' },
      lines.map(function(l, idx) {
        return React.createElement('div', { key: l.slug, className: 'bg-black border border-white/10 rounded-lg px-3 py-2' },
          React.createElement('div', { className: 'flex flex-wrap items-center gap-2' },
          React.createElement('span', { className: 'text-sm text-neutral-200 flex-1 min-w-[160px] truncate' }, l.name,
            l.presale ? React.createElement('span', { className: 'text-amber-400/90 text-xs ml-2' }, 'pre-sale') : null),
          (l.groups && l.groups.length) ? null : React.createElement('input', {
            value: l.variant, onChange: function(e) { patchLine(idx, 'variant', e.target.value); },
            placeholder: 'variant', 'data-testid': 'mo-line-variant',
            className: 'w-28 bg-neutral-950 border border-white/10 rounded px-2 py-1 text-xs text-white'
          }),
          React.createElement('input', {
            type: 'number', min: 1, value: l.quantity,
            onChange: function(e) { patchLine(idx, 'quantity', e.target.value); },
            'data-testid': 'mo-line-qty',
            className: 'w-16 bg-neutral-950 border border-white/10 rounded px-2 py-1 text-xs text-white'
          }),
          React.createElement('div', { className: 'flex items-center gap-1' },
            React.createElement('span', { className: 'text-neutral-500 text-xs' }, '$'),
            React.createElement('input', {
              type: 'number', step: '0.01', min: 0, value: l.unit_price,
              onChange: function(e) { patchLine(idx, 'unit_price', e.target.value); },
              'data-testid': 'mo-line-price',
              className: 'w-20 bg-neutral-950 border border-white/10 rounded px-2 py-1 text-xs text-white'
            })
          ),
          React.createElement('span', { className: 'text-neutral-300 text-sm w-20 text-right' }, money(Number(l.unit_price) * Number(l.quantity))),
          React.createElement('button', {
            type: 'button', onClick: function() { removeLine(idx); }, 'data-testid': 'mo-line-remove',
            className: 'text-red-400 hover:text-red-300'
          }, React.createElement(Trash2, { className: 'w-4 h-4' }))
          ),
          (l.groups && l.groups.length) ? React.createElement('div', { className: 'flex flex-wrap gap-2 mt-2' },
            l.groups.map(function(g) {
              return React.createElement('select', {
                key: g.name,
                value: (l.picks || {})[g.name] || '',
                onChange: function(e) { pickVariant(idx, g.name, e.target.value); },
                'data-testid': 'mo-line-variant-select',
                title: g.name,
                className: 'flex-1 min-w-[180px] bg-neutral-950 border border-white/10 rounded px-2 py-1.5 text-xs text-white'
              },
                (g.options || []).map(function(opt) {
                  return React.createElement('option', { key: opt, value: opt }, g.name + ': ' + opt);
                })
              );
            })
          ) : null
        );
      })
      )
    ) : React.createElement('p', { className: 'text-neutral-600 text-sm mb-5' }, 'No items yet.'),

    // ---- shipping ----
    hasPhysical ? React.createElement('div', { className: 'border-t border-white/10 pt-4 mb-5' },
      React.createElement('label', { className: LABEL }, 'Shipping address'),
      React.createElement('div', { className: 'grid sm:grid-cols-2 gap-2 mb-3' },
        [['first_name', 'First name'], ['last_name', 'Last name'], ['street_address', 'Street'],
         ['extended_address', 'Apt / unit'], ['locality', 'City'], ['region', 'State / region'],
         ['postal_code', 'Postal code'], ['country_code_alpha2', 'Country code (US, MX…)']].map(function(f) {
          return React.createElement('input', {
            key: f[0], value: address[f[0]] || '', placeholder: f[1],
            'data-testid': 'mo-addr-' + f[0].replace(/_/g, '-'),
            onChange: function(e) {
              var v = e.target.value;
              setAddress(function(prev) { return Object.assign({}, prev, { [f[0]]: v }); });
            },
            className: FIELD
          });
        })
      ),
      React.createElement('button', {
        type: 'button', onClick: getShippingQuote, disabled: busy === 'quote' || !lines.length,
        'data-testid': 'mo-get-rates',
        className: 'text-xs text-[#d4af37] hover:text-white border border-[#d4af37]/40 rounded-full px-3 py-1.5 disabled:opacity-40'
      }, busy === 'quote' ? 'Getting rates…' : 'Get shipping rates & tax for this address'),
      shippingOptions.length ? React.createElement('div', { className: 'mt-3' },
        React.createElement('select', {
          value: shippingId, onChange: function(e) { pickShipping(e.target.value); },
          className: FIELD, 'data-testid': 'mo-shipping-option'
        },
          shippingOptions.map(function(o) {
            return React.createElement('option', { key: o.id, value: o.id }, o.name + ' — ' + money(o.rate_usd));
          })
        )
      ) : null
    ) : null,

    // ---- money ----
    React.createElement('div', { className: 'grid sm:grid-cols-3 gap-3 mb-5 border-t border-white/10 pt-4' },
      React.createElement('div', null,
        React.createElement('label', { className: LABEL }, 'Shipping $'),
        React.createElement('input', {
          type: 'number', step: '0.01', value: shippingCost,
          onChange: function(e) { setShippingCost(e.target.value); },
          className: FIELD, 'data-testid': 'mo-shipping-cost'
        })
      ),
      React.createElement('div', null,
        React.createElement('label', { className: LABEL }, taxMode === 'amount' ? 'Tax $' : 'Tax %'),
        React.createElement('div', { className: 'flex gap-2' },
          React.createElement('input', {
            type: 'number', step: '0.01', value: taxValue,
            onChange: function(e) { setTaxValue(e.target.value); },
            className: FIELD, 'data-testid': 'mo-tax-value'
          }),
          React.createElement('button', {
            type: 'button',
            onClick: function() { setTaxMode(taxMode === 'amount' ? 'rate' : 'amount'); },
            'data-testid': 'mo-tax-mode',
            className: 'shrink-0 px-3 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg'
          }, taxMode === 'amount' ? '$' : '%')
        ),
        React.createElement('p', { className: 'text-neutral-600 text-[11px] mt-1' },
          'Digital files are never taxed' + (suggestedRate ? ' · suggested ' + (suggestedRate * 100).toFixed(1) + '%' : ''))
      ),
      React.createElement('div', null,
        React.createElement('label', { className: LABEL }, discountMode === 'percent' ? 'Discount %' : 'Discount $'),
        React.createElement('div', { className: 'flex gap-2' },
          React.createElement('input', {
            type: 'number', step: '0.01', value: discount,
            onChange: function(e) { setDiscount(e.target.value); },
            className: FIELD, 'data-testid': 'mo-discount'
          }),
          React.createElement('button', {
            type: 'button',
            onClick: function() { setDiscountMode(discountMode === 'amount' ? 'percent' : 'amount'); },
            'data-testid': 'mo-discount-mode',
            className: 'shrink-0 px-3 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg'
          }, discountMode === 'amount' ? '$' : '%')
        ),
        discountMode === 'percent' ? React.createElement('p', { className: 'text-neutral-600 text-[11px] mt-1' },
          '− ' + money(discountNum) + ' off the items') : null
      )
    ),

    // ---- payment ----
    React.createElement('label', { className: LABEL }, 'How is the customer paying?'),
    React.createElement('div', { className: 'flex flex-wrap gap-2 mb-3' },
      [['paypal', 'PayPal link'], ['bank_transfer', 'Bank transfer'], ['cash', 'Cash']].map(function(m) {
        return React.createElement('button', {
          key: m[0], type: 'button', onClick: function() { setMethod(m[0]); },
          'data-testid': 'mo-method-' + m[0].replace('_', '-'),
          className: 'px-4 py-2 rounded-full text-sm border transition-colors ' +
            (method === m[0] ? 'bg-[#d4af37] text-black border-[#d4af37] font-semibold' : 'text-neutral-300 border-white/15 hover:border-white/40')
        }, m[1]);
      })
    ),
    React.createElement('div', { className: 'space-y-2 mb-4' },
      method === 'paypal' ? React.createElement('label', { className: 'flex items-center gap-2 text-neutral-400 text-sm cursor-pointer' },
        React.createElement('input', {
          type: 'checkbox', checked: emailLink, disabled: markPaid,
          onChange: function(e) { setEmailLink(e.target.checked); },
          className: 'w-4 h-4 accent-[#d4af37]', 'data-testid': 'mo-email-link'
        }), 'Email the pay link to the customer'
      ) : null,
      React.createElement('label', { className: 'flex items-center gap-2 text-neutral-400 text-sm cursor-pointer' },
        React.createElement('input', {
          type: 'checkbox', checked: markPaid,
          onChange: function(e) { setMarkPaid(e.target.checked); },
          className: 'w-4 h-4 accent-[#d4af37]', 'data-testid': 'mo-mark-paid'
        }), 'Already paid — send the receipt and download links now'
      )
    ),

    React.createElement('div', null,
      React.createElement('label', { className: LABEL }, 'Notes (internal)'),
      React.createElement('textarea', {
        value: notes, onChange: function(e) { setNotes(e.target.value); }, rows: 2,
        className: FIELD, 'data-testid': 'mo-notes'
      })
    ),

    React.createElement('div', { className: 'flex items-center justify-between mt-4 mb-3 bg-black border border-white/10 rounded-xl px-4 py-3' },
      React.createElement('span', { className: 'text-neutral-400 text-sm' }, 'Total'),
      React.createElement('span', { className: 'text-[#d4af37] text-xl font-semibold', 'data-testid': 'mo-total' }, money(total))
    ),

    editing ? React.createElement('div', { className: 'flex flex-wrap gap-2' },
      React.createElement('button', {
        onClick: function() { saveEdits(false); }, disabled: busy === 'save' || !lines.length,
        'data-testid': 'mo-save-edits',
        className: 'flex-1 min-w-[160px] flex items-center justify-center gap-2 bg-[#d4af37] text-black font-semibold py-3 rounded-full hover:bg-white disabled:opacity-40 transition-colors'
      }, busy === 'save' ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : null, 'Save changes'),
      React.createElement('button', {
        onClick: function() { saveEdits(true); }, disabled: busy === 'save' || !lines.length,
        'data-testid': 'mo-save-and-resend',
        className: 'flex-1 min-w-[160px] flex items-center justify-center gap-2 border border-[#d4af37]/50 text-[#d4af37] font-semibold py-3 rounded-full hover:bg-[#d4af37]/10 disabled:opacity-40 transition-colors'
      }, React.createElement(Mail, { className: 'w-4 h-4' }), 'Save & re-send pay link')
    ) : React.createElement('button', {
      onClick: createOrder, disabled: busy === 'create' || !lines.length,
      'data-testid': 'mo-create',
      className: 'w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black font-semibold py-3 rounded-full hover:bg-white disabled:opacity-40 transition-colors'
    },
      busy === 'create' ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : null,
      busy === 'create' ? 'Creating…' : (markPaid ? 'Create paid order & send links' : 'Create order')
    ),

    msg ? React.createElement('div', {
      'data-testid': 'mo-message',
      className: 'mt-3 px-4 py-2 rounded-lg text-sm ' + (msg.type === 'success'
        ? 'bg-green-500/10 border border-green-500/30 text-green-300'
        : 'bg-red-500/10 border border-red-500/30 text-red-300')
    }, (msg.type === 'success' ? '✅ ' : '⚠️ ') + msg.text) : null,

    created ? React.createElement('div', { className: 'mt-4 bg-black border border-[#d4af37]/30 rounded-xl p-4', 'data-testid': 'mo-created' },
      React.createElement('p', { className: 'text-white text-sm font-semibold mb-2' }, created.order_number + ' · ' + money(created.total) + ' · ' + (created.status || '')),
      created.status === 'pending_payment' ? React.createElement('div', null,
        React.createElement('div', { className: 'flex items-center gap-2 mb-3' },
          React.createElement(LinkIcon, { className: 'w-3.5 h-3.5 text-[#d4af37] shrink-0' }),
          React.createElement('code', { className: 'text-[#d4af37] text-xs break-all', 'data-testid': 'mo-pay-url' }, created.pay_url)
        ),
        React.createElement('div', { className: 'flex flex-wrap gap-2' },
          React.createElement('button', {
            type: 'button',
            onClick: function() { navigator.clipboard.writeText(created.pay_url); setMsg({ type: 'success', text: 'Pay link copied' }); },
            'data-testid': 'mo-copy-link',
            className: 'flex items-center gap-1.5 text-xs text-neutral-200 border border-white/15 hover:border-white/40 rounded-full px-3 py-1.5'
          }, React.createElement(Copy, { className: 'w-3.5 h-3.5' }), 'Copy link'),
          React.createElement('button', {
            type: 'button', onClick: emailTheLink, disabled: busy === 'link',
            'data-testid': 'mo-email-pay-link',
            className: 'flex items-center gap-1.5 text-xs text-neutral-200 border border-white/15 hover:border-white/40 rounded-full px-3 py-1.5'
          }, React.createElement(Mail, { className: 'w-3.5 h-3.5' }), busy === 'link' ? 'Sending…' : 'Email the link'),
          React.createElement('button', {
            type: 'button', onClick: markCreatedPaid, disabled: busy === 'paid',
            'data-testid': 'mo-confirm-paid',
            className: 'flex items-center gap-1.5 text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25 rounded-full px-3 py-1.5'
          }, React.createElement(CheckCircle2, { className: 'w-3.5 h-3.5' }), busy === 'paid' ? 'Confirming…' : 'Confirm payment received')
        )
      ) : React.createElement('p', { className: 'text-emerald-300 text-xs' }, 'Paid — receipt and any download links have been emailed.'),
      React.createElement('button', {
        type: 'button', onClick: reset, 'data-testid': 'mo-new-order',
        className: 'mt-3 text-xs text-neutral-500 hover:text-white underline'
      }, 'Start another order')
    ) : null
  );
}
